import { Avatar, Button, Col, Row, Text } from 'components';
import { format } from 'date-fns';
import { theme } from 'lib';
import { useState } from 'react';
import styled from 'styled-components';
import { Games, GamesPanel, Header, P4PReplayPanel, RewardTypePanel } from './';

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: ${theme.GREY_10};
  border-radius: 12px;
  margin-bottom: 24px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  padding: 12px 0;
  border-bottom: 1px solid ${theme.GREY_20};

  &:last-child {
    border-bottom: none;
  }
`;

const TeamDetail = ({ team, onEdit }) => {
  const [gamesPanelVisible, setGamesPanelVisible] = useState(false);
  const [rewardGiftsVisible, setRewardGiftsVisible] = useState(false);
  const [p4pReplayVisible, setP4PReplayVisible] = useState(false);
  const [rewardTypeVisible, setRewardTypeVisible] = useState(false);

  if (!team) return null;

  const handleViewMoreGames = () => {
    setGamesPanelVisible(true);
  };

  const handleCloseGamesPanel = () => {
    setGamesPanelVisible(false);
  };

  return (
    <Col pad="24px" pos="relative">
      <Row start marg="0 0 24px">
        <Col noFlex marg="0 24px 0 0" style={{ zIndex: 1, position: 'relative' }}>
          <Avatar user={team} avatar={team.avatar} size={90} />
        </Col>

        <Header team={team} />
      </Row>

      <Row between center marg="0 0 24px">
        <Text h4 bold>
          Team Information
        </Text>
        <Row noFlex>
          {/* Hidden Reward Gifts button */}
          {/* <Button
            text="Reward Gifts"
            onClick={() => setRewardGiftsVisible(true)}
            bordered
            icon="storeOutline"
            marg="0 12px 0 0"
            style={{ width: 'fit-content' }}
          /> */}
          <Button
            text="Reward Types"
            onClick={() => setRewardTypeVisible(true)}
            bordered
            icon="gift"
            marg="0 12px 0 0"
            style={{ width: 'fit-content' }}
          />
          <Button
            text="P4P Replay"
            onClick={() => setP4PReplayVisible(true)}
            bordered
            icon="play"
            marg="0 12px 0 0"
            style={{ width: 'fit-content' }}
          />
          <Button
            text="Edit Team"
            onClick={onEdit}
            bordered
            icon="edit"
            style={{ width: 'fit-content' }}
          />
        </Row>
      </Row>

      <InfoSection>
        <InfoRow>
          <Col noFlex wid="30%">
            <Text b2 med col={theme.GREY_60}>
              Team Name
            </Text>
          </Col>
          <Col>
            <Text b2>{team.name || '-'}</Text>
          </Col>
        </InfoRow>

        <InfoRow>
          <Col noFlex wid="30%">
            <Text b2 med col={theme.GREY_60}>
              Username
            </Text>
          </Col>
          <Col>
            <Text b2>@{team.username || '-'}</Text>
          </Col>
        </InfoRow>

        <InfoRow>
          <Col noFlex wid="30%">
            <Text b2 med col={theme.GREY_60}>
              Email
            </Text>
          </Col>
          <Col>
            <Text b2>{team.email || '-'}</Text>
          </Col>
        </InfoRow>

        <InfoRow>
          <Col noFlex wid="30%">
            <Text b2 med col={theme.GREY_60}>
              League/Division
            </Text>
          </Col>
          <Col>
            <Text b2>{team.league || '-'}</Text>
          </Col>
        </InfoRow>

        {team.bio && (
          <InfoRow>
            <Col noFlex wid="30%">
              <Text b2 med col={theme.GREY_60}>
                Bio
              </Text>
            </Col>
            <Col>
              <Text b2>{team.bio}</Text>
            </Col>
          </InfoRow>
        )}
      </InfoSection>

      <Text h4 bold marg="24px 0 16px">
        Additional Information
      </Text>

      <InfoSection noFlex>
        {team.birthday && (
          <InfoRow>
            <Col noFlex wid="30%">
              <Text b2 med col={theme.GREY_60}>
                Founded Date
              </Text>
            </Col>
            <Col>
              <Text b2>{format(new Date(team.birthday), 'MMMM dd, yyyy')}</Text>
            </Col>
          </InfoRow>
        )}

        {team.country && (
          <InfoRow>
            <Col noFlex wid="30%">
              <Text b2 med col={theme.GREY_60}>
                Country
              </Text>
            </Col>
            <Col>
              <Text b2>{team.country}</Text>
            </Col>
          </InfoRow>
        )}

        {team.phone && (
          <InfoRow>
            <Col noFlex wid="30%">
              <Text b2 med col={theme.GREY_60}>
                Phone
              </Text>
            </Col>
            <Col>
              <Text b2>
                {team.phoneNation && `${team.phoneNation} `}
                {team.phone}
              </Text>
            </Col>
          </InfoRow>
        )}

        {team.sports?.length > 0 && (
          <InfoRow>
            <Col noFlex wid="30%">
              <Text b2 med col={theme.GREY_60}>
                Sports
              </Text>
            </Col>
            <Col>
              {team.sports.map((sport, i) => (
                <Text key={i} b2 marg={i > 0 ? '4px 0 0' : '0'}>
                  {sport.sport}
                  {sport.position && ` - ${sport.position}`}
                </Text>
              ))}
            </Col>
          </InfoRow>
        )}

        <InfoRow>
          <Col noFlex wid="30%">
            <Text b2 med col={theme.GREY_60}>
              Email Verified
            </Text>
          </Col>
          <Col>
            <Text b2>{team.verified?.email ? '✓ Verified' : '✗ Not verified'}</Text>
          </Col>
        </InfoRow>

        <InfoRow>
          <Col noFlex wid="30%">
            <Text b2 med col={theme.GREY_60}>
              ID Verified
            </Text>
          </Col>
          <Col>
            <Text b2>{team.verified?.id ? '✓ Verified' : '✗ Not verified'}</Text>
          </Col>
        </InfoRow>

        {team.createdAt && (
          <InfoRow>
            <Col noFlex wid="30%">
              <Text b2 med col={theme.GREY_60}>
                Joined
              </Text>
            </Col>
            <Col>
              <Text b2>{format(new Date(team.createdAt), 'MMMM dd, yyyy h:mm a')}</Text>
            </Col>
          </InfoRow>
        )}
      </InfoSection>

      <Games team={team} limit={5} onViewMore={handleViewMoreGames} />

      <GamesPanel visible={gamesPanelVisible} closeModal={handleCloseGamesPanel} team={team} />

      {/* Hidden Reward Gifts Modal */}
      {/* {rewardGiftsVisible && (
        <RewardGiftsModal team={team} onClose={() => setRewardGiftsVisible(false)} />
      )} */}

      <P4PReplayPanel
        visible={p4pReplayVisible}
        closeModal={() => setP4PReplayVisible(false)}
        team={team}
      />

      <RewardTypePanel
        visible={rewardTypeVisible}
        closeModal={() => setRewardTypeVisible(false)}
        team={team}
      />
    </Col>
  );
};

export default TeamDetail;
